import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Bot, Play, Square } from "lucide-react";
import { useScriptsApi } from "../../api/scriptsApi";

const REASON_LABEL = {
  AGENT_TAKEOVER: "desligado quando o atendimento assumiu",
  MANUAL: "desligado manualmente",
  FINISHED: "roteiro concluído",
};

/**
 * Liga e desliga o roteiro automático da conversa. Fica no cabeçalho do chat
 * porque é ali que o atendente percebe que o robô está falando por ele.
 */
const BotControl = ({ conversationId }) => {
  const api = useScriptsApi();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [choosing, setChoosing] = useState(false);

  const load = useCallback(async () => {
    if (!conversationId) return;
    try {
      setState(await api.getConversationState(conversationId));
    } catch {
      setState(null);
    }
  }, [api, conversationId]);

  useEffect(() => {
    load();
  }, [load]);

  // O robô se desliga sozinho quando alguém responde; sem recarregar, o botão
  // continuaria mostrando "ativo" depois disso.
  useEffect(() => {
    if (!conversationId) return undefined;
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [conversationId, load]);

  if (!state) return null;

  const apply = async (payload) => {
    setBusy(true);
    try {
      setState(await api.setConversationBot(conversationId, payload));
      setChoosing(false);
      toast.success(payload.active === false ? "Robô desligado." : "Robô ligado.");
    } catch (error) {
      toast.error(error.message || "Não foi possível alterar o robô.");
    } finally {
      setBusy(false);
    }
  };

  if (state.active) {
    return (
      <button
        onClick={() => apply({ active: false })}
        disabled={busy}
        className="flex items-center gap-1.5 rounded-lg bg-green-100 px-3 py-2 text-sm font-medium text-green-800 transition-colors hover:bg-green-200 disabled:opacity-60 dark:bg-green-900/30 dark:text-green-300"
        title={`${state.run?.scriptName} — passo ${(state.run?.currentStep ?? 0) + 1} de ${state.run?.totalSteps}`}
      >
        <Bot className="h-4 w-4" />
        <span className="hidden sm:inline">
          Robô ativo · {state.run?.currentStep ?? 0}/{state.run?.totalSteps ?? 0}
        </span>
        <Square className="h-3.5 w-3.5" />
      </button>
    );
  }

  if (choosing) {
    return (
      <div className="flex items-center gap-1">
        <select
          defaultValue={state.run?.scriptId || ""}
          onChange={(event) =>
            event.target.value
            && apply({ active: true, scriptId: event.target.value, restart: true })
          }
          disabled={busy}
          className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">Escolha o roteiro...</option>
          {state.scripts.map((script) => (
            <option key={script.id} value={script.id}>
              {script.name}
            </option>
          ))}
        </select>
        <button
          onClick={() => setChoosing(false)}
          className="rounded p-1 text-xs text-gray-500 hover:underline"
        >
          cancelar
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => {
        // Já houve roteiro nesta conversa: religa de onde parou, sem perguntar.
        if (state.run?.scriptId) apply({ active: true });
        else setChoosing(true);
      }}
      disabled={busy || state.scripts.length === 0}
      className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
      title={
        state.run?.deactivatedBy
          ? `Robô ${REASON_LABEL[state.run.deactivatedBy] || "desligado"}`
          : "Ligar o roteiro automático"
      }
    >
      <Play className="h-4 w-4" />
      <span className="hidden sm:inline">
        {state.run?.scriptId ? "Retomar robô" : "Ligar robô"}
      </span>
    </button>
  );
};

export default BotControl;
